import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CarbonTable } from 'src/app/components/carbon-table/carbon-table.component';
import { OperationTransferenceService } from 'src/app/services/operation/operation-transference.service';
import { CarbonOperation } from 'src/app/shared/interfaces/carbon-operation';
import { Pagination } from 'src/app/shared/interfaces/pagination';
import {
  OperationTypeBadge,
  resolveOperationTypeBadge,
} from '../../operation-list/shared/operation-type-badge.utils';
import { OperationFilter } from '../../shared/operation-filter';
import { ClickEnumConfirmation, EmitClickConfirmation } from '../shared/emit-click.interface';
import { OperationConfirmationService } from '../shared/operation-confirmation.service';
import {
  getCarbonCreditCodeValue,
  OperationControlNumberLines,
  splitOperationControlNumber,
} from '../shared/operation-control-number.utils';

@Component({
  standalone: false,
  selector: 'app-operation-confirmation-table',
  templateUrl: './operation-confirmation-table.component.html',
  styleUrls: ['./operation-confirmation-table.component.scss'],
})
export class OperationConfirmationTableComponent extends CarbonTable<CarbonOperation> implements OnInit, OnDestroy {
  private static readonly PAGE_SIZE = 10;

  readonly ClickEnumConfirmation = ClickEnumConfirmation;

  @Output() emitClick = new EventEmitter<EmitClickConfirmation>();

  private readonly destroy$ = new Subject<void>();

  override pagination: Pagination<CarbonOperation> = OperationConfirmationTableComponent.createPagination();

  constructor(
    private readonly operationTransferenceService: OperationTransferenceService,
    private readonly confirmationService: OperationConfirmationService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.filterInit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  override resetPagination(): void {
    this.pagination = OperationConfirmationTableComponent.createPagination();
  }

  get operationsFoundLabel(): string {
    const count = this.totalItens ?? 0;
    return count === 1
      ? '1 resultado encontrado para o filtro selecionado'
      : `${count} resultados encontrados para o filtro selecionado`;
  }

  get recordsPerPageLabel(): string {
    const pageSize = this.pagination.size ?? OperationConfirmationTableComponent.PAGE_SIZE;
    return pageSize === 1
      ? 'Mostrando 1 registro por página'
      : `Mostrando ${pageSize} registros por página`;
  }

  trackByOperationCode(_index: number, operation: CarbonOperation): string {
    return `${operation.operationCode ?? 'operation'}-${_index}`;
  }

  formatQuantity(value?: number | null): string {
    if (value === null || value === undefined) {
      return '-';
    }

    return `${value}`.padStart(2, '0');
  }

  getOperationTypeBadge(operation: CarbonOperation): OperationTypeBadge {
    return resolveOperationTypeBadge(operation);
  }

  getCarbonCreditCode(operation: CarbonOperation): string {
    return getCarbonCreditCodeValue(operation.carbonCreditCode);
  }

  getOperationControlNumber(operation: CarbonOperation): string {
    const value = operation.operationCode?.trim();
    return value && value !== '-' ? value : '';
  }

  getOperationControlNumberLines(operation: CarbonOperation): OperationControlNumberLines | null {
    const value = this.getOperationControlNumber(operation);
    return value ? splitOperationControlNumber(value) : null;
  }

  getOperationControlNumberAriaLabel(operation: CarbonOperation): string {
    const value = this.getOperationControlNumber(operation);
    return value
      ? `Número de controle ${value}. Clique para copiar.`
      : 'Número de controle indisponível';
  }

  getApproveLabel(operation: CarbonOperation): string {
    return `Aprovar registro ${operation.operationCode || ''}`.trim();
  }

  getRejectLabel(operation: CarbonOperation): string {
    return `Rejeitar registro ${operation.operationCode || ''}`.trim();
  }

  getDetailsLabel(operation: CarbonOperation): string {
    return `Visualizar detalhes do registro ${operation.operationCode || ''}`.trim();
  }

  copyOperationControlNumber(operation: CarbonOperation, event: Event): void {
    event.stopPropagation();

    const value = this.getOperationControlNumber(operation);
    if (!value) {
      return;
    }

    void navigator.clipboard.writeText(value);
  }

  emitAction(type: ClickEnumConfirmation, row: CarbonOperation): void {
    this.emitClick.emit({ type, row });
  }

  onPaginationChange(event: Event): void {
    const customEvent = event as CustomEvent<{ current: number; previous: number }>;
    const nextPage = customEvent.detail?.current;

    if (nextPage && nextPage !== this.pagination.page) {
      this.onPageChange(nextPage);
    }
  }

  onPageChange(currentPage: number): void {
    this.pagination.page = currentPage;
    const filters = this.confirmationService.filters.getValue();
    this.getOperationPendingApproval(filters, this.pagination);
  }

  reloadTable(): void {
    this.resetPagination();
    const filters = this.confirmationService.filters.getValue();
    this.getOperationPendingApproval(filters, this.pagination);
  }

  refreshCurrentPage(): void {
    const filters = this.confirmationService.filters.getValue();
    this.getOperationPendingApproval(filters, this.pagination);
  }

  updateOperationStatus(operationCode: string, statusCode: string): void {
    const index = this.itens.findIndex((operation) => operation.operationCode === operationCode);

    if (index < 0) {
      return;
    }

    this.itens[index] = { ...this.itens[index], statusCode };
    this.itens = [...this.itens];
  }

  isPendingApproval(operation: CarbonOperation): boolean {
    const status = operation.statusCode?.toLowerCase() ?? '';
    return status.includes('pend') || status.includes('confirma');
  }

  /** @deprecated Use reloadTable() — mantido para compatibilidade com o container. */
  resetB3Table(): void {
    this.reloadTable();
  }

  private static createPagination(): Pagination<CarbonOperation> {
    return {
      page: 1,
      size: OperationConfirmationTableComponent.PAGE_SIZE,
      results: [],
      total: 0,
    };
  }

  private getOperationPendingApproval(
    filter: OperationFilter,
    pagination: Pagination<CarbonOperation>,
  ): void {
    this.operationTransferenceService
      .getOperationPendingApproval(filter, true, pagination)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.itens = response.results;
        this.totalItens = response.total ?? 0;
        this.handleTotalPages(null);
      });
  }

  private filterInit(): void {
    this.confirmationService.filters
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => {
        this.resetPagination();
        this.getOperationPendingApproval(filters, this.pagination);
      });
  }
}
